import StepUpDialog from '@/components/Mfa/StepUpDialog';
import {
  MfaMode,
  ViewerMfaDocument,
  ViewerMfaQuery,
} from '@/generated/graphql';
import { setStepUpHandler, StepUpRequest } from '@/utils/step-up';
import { ApolloClient, useApolloClient } from '@apollo/client';
import React, { useEffect, useState } from 'react';

interface OpenRequest extends StepUpRequest {
  resolve: (passed: boolean) => void;
}

/**
 * True when the cached viewer.mfa says MFA is off for this user. The
 * server never asks for a step-up then; this keeps a stray error from
 * opening a prompt the user cannot answer. Reads the cache only.
 */
export function mfaOffInCache(client: ApolloClient<object>): boolean {
  try {
    const cached = client.readQuery<ViewerMfaQuery>({
      query: ViewerMfaDocument,
    });
    return cached?.viewer?.mfa?.mode === MfaMode.Off;
  } catch {
    return false;
  }
}

/**
 * Mounted once in _app. The Apollo step-up link (utils/apollo-client.ts)
 * opens it through requestStepUp when any request fails with
 * auth:mfa_step_up_required, and retries the request after a pass.
 */
export default function StepUpPrompt(): JSX.Element {
  const client = useApolloClient();
  const [request, setRequest] = useState<OpenRequest | null>(null);

  useEffect(() => {
    setStepUpHandler((details) =>
      mfaOffInCache(client)
        ? Promise.resolve(false)
        : new Promise<boolean>((resolve) =>
            setRequest({ ...details, resolve }),
          ),
    );
    return () => setStepUpHandler(null);
  }, [client]);

  function finish(passed: boolean) {
    request?.resolve(passed);
    setRequest(null);
  }

  return (
    <StepUpDialog
      open={Boolean(request)}
      methods={request?.methods}
      onVerified={() => finish(true)}
      onCancel={() => finish(false)}
    />
  );
}
